import React, { useRef, useCallback, useEffect, useState } from 'react';
import { MapPin, X, Map } from 'lucide-react';

// Google Maps type declarations
declare global {
    interface Window {
        google: any;
    }
}

interface ServiceLocationMapProps {
    isEditing?: boolean;
    onLocationChange?: (lat: number, lng: number, address: string, city: string, parish: string) => void;
    onConfirm?: (lat: number, lng: number, address: string, city: string, parish: string) => void;
    initialAddress?: string;
    initialCoordinates?: { lat: number; lng: number };
    publicView?: boolean;
}

const ServiceLocationMap: React.FC<ServiceLocationMapProps> = ({
    isEditing = false,
    onLocationChange,
    onConfirm,
    initialAddress,
    initialCoordinates,
    publicView = false
}) => {
    const [showMap, setShowMap] = useState(true);
    const [mapCenter, setMapCenter] = useState(initialCoordinates || { lat: 18.1096, lng: -77.2975 });
    const [currentLocation, setCurrentLocation] = useState<{ lat: number, lng: number, address: string, city: string, parish: string }>({
        lat: initialCoordinates?.lat || 0,
        lng: initialCoordinates?.lng || 0,
        address: initialAddress || '',
        city: '',
        parish: ''
    });
    const [mapLoaded, setMapLoaded] = useState(false);
    const [mapInitialized, setMapInitialized] = useState(false);
    const prevShowMapRef = useRef(isEditing || publicView);

    const mapRef = useRef<HTMLDivElement>(null);
    const mapInstanceRef = useRef<google.maps.Map | null>(null);
    const markerRef = useRef<google.maps.Marker | null>(null);
    const circleRef = useRef<google.maps.Circle | null>(null);

    // Sync map re-initialization on edit mode changes
    useEffect(() => {
        const shouldShow = isEditing || publicView;
        if (shouldShow && !prevShowMapRef.current) {
            // Edit mode is turning on or transitioning to visible view
            // Reset mapInitialized so map re-initializes with proper dimensions
            console.log('ServiceLocationMap: View/Edit mode refresh — resetting map for re-initialization');
            setMapInitialized(false);
            mapInstanceRef.current = null;
        }
        prevShowMapRef.current = shouldShow;
    }, [isEditing, publicView]);

    // Track the last externally-applied coordinates (by value, not object ref)
    const prevExternalCoordsRef = useRef<{ lat: number; lng: number } | undefined>(undefined);

    // Only update marker when the actual lat/lng VALUES change from an external source.
    // Without this, every parent re-render creates a new {lat, lng} object → useEffect fires
    // → markerRef.setPosition() snaps the marker back mid-drag.
    useEffect(() => {
        if (!initialCoordinates) return;

        const prev = prevExternalCoordsRef.current;
        const latChanged = !prev || prev.lat !== initialCoordinates.lat;
        const lngChanged = !prev || prev.lng !== initialCoordinates.lng;

        if (!latChanged && !lngChanged) return; // Values didn't change — skip

        console.log('ServiceLocationMap: External coords changed, updating map:', initialCoordinates);
        prevExternalCoordsRef.current = { lat: initialCoordinates.lat, lng: initialCoordinates.lng };

        setMapCenter(initialCoordinates);

        if (mapInstanceRef.current) {
            mapInstanceRef.current.setCenter(initialCoordinates);
            if (markerRef.current) {
                markerRef.current.setPosition(initialCoordinates);
            }
            if (circleRef.current) {
                circleRef.current.setCenter(initialCoordinates);
            }
        }
    }, [initialCoordinates]);

    // Reverse geocode coordinates to get address
    const reverseGeocode = useCallback((lat: number, lng: number) => {
        if (!window.google || !window.google.maps) return;

        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: { lat, lng } }, (results: any, status: any) => {
            if (status === 'OK' && results && results[0]) {
                const address = results[0].formatted_address;

                let city = '';
                let parish = '';

                results[0].address_components.forEach((component: any) => {
                    const types = component.types;
                    if (types.includes('locality')) {
                        city = component.long_name;
                    }
                    if (types.includes('administrative_area_level_1')) {
                        let cleanName = component.long_name;
                        cleanName = cleanName.replace(/ Parish/i, '');
                        cleanName = cleanName.replace(/^Saint /i, 'St. ');
                        cleanName = cleanName.replace(/^St /i, 'St. ');
                        parish = cleanName;
                    }
                });

                if (onLocationChange) {
                    onLocationChange(lat, lng, address, city, parish);
                }
                setCurrentLocation({ lat, lng, address, city, parish });
            }
        });
    }, [onLocationChange]);

    const handleConfirm = () => {
        if (onConfirm) {
            onConfirm(currentLocation.lat, currentLocation.lng, currentLocation.address, currentLocation.city, currentLocation.parish);
        }
    };

    const createMarker = useCallback((position: { lat: number, lng: number }, map: google.maps.Map) => {
        if (markerRef.current) {
            markerRef.current.setMap(null);
        }

        const markerOptions: any = {
            position: position,
            map,
            title: 'Your Location',
            draggable: false,
        };

        const marker = new window.google.maps.Marker(markerOptions);
        markerRef.current = marker;
    }, []);

    const createCircle = useCallback((center: { lat: number, lng: number }, map: google.maps.Map) => {
        if (circleRef.current) {
            circleRef.current.setMap(null);
        }

        const circle = new window.google.maps.Circle({
            strokeColor: '#3498db',
            strokeOpacity: 0.8,
            strokeWeight: 2,
            fillColor: '#3498db',
            fillOpacity: 0.35,
            map,
            center: center,
            radius: 2000,
            clickable: false
        });
        circleRef.current = circle;
    }, []);

    // Initialize Google Maps
    const initializeMap = useCallback(() => {
        if (!mapRef.current || !window.google || !window.google.maps || mapInitialized) return;

        console.log('ServiceLocationMap: Initializing map with center:', mapCenter);
        const map = new window.google.maps.Map(mapRef.current, {
            center: mapCenter,
            zoom: publicView ? 12 : 13,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
            gestureHandling: 'cooperative',
            scrollwheel: true,
            disableDoubleClickZoom: false,
            styles: [
                {
                    featureType: 'poi',
                    elementType: 'labels',
                    stylers: [{ visibility: 'off' }]
                }
            ]
        });

        mapInstanceRef.current = map;
        setMapInitialized(true);

        // Trigger resize after a short delay to ensure container has correct dimensions
        setTimeout(() => {
            if (mapInstanceRef.current) {
                window.google.maps.event.trigger(mapInstanceRef.current, 'resize');
                mapInstanceRef.current.setCenter(mapCenter);
            }
        }, 300);

        if (initialCoordinates) {
            if (publicView) {
                createCircle(initialCoordinates, map);
            } else {
                createMarker(initialCoordinates, map);
            }
        }
    }, [mapCenter, initialCoordinates, mapInitialized, createMarker, createCircle]);

    // Update map listeners when editing mode changes
    useEffect(() => {
        if (!mapInstanceRef.current || !window.google) return;

        const map = mapInstanceRef.current;
        let clickListener: google.maps.MapsEventListener | null = null;

        if (isEditing) {
            clickListener = map.addListener('click', (event: google.maps.MapMouseEvent) => {
                if (event.latLng) {
                    const lat = event.latLng.lat();
                    const lng = event.latLng.lng();
                    createMarker({ lat, lng }, map);
                    reverseGeocode(lat, lng);
                }
            });
        }

        return () => {
            if (clickListener) {
                window.google.maps.event.removeListener(clickListener);
            }
        };
    }, [isEditing, createMarker, reverseGeocode]);

    // Load Google Maps script
    useEffect(() => {
        if (!showMap) return;

        console.log('ServiceLocationMap: showMap=true, checking Google Maps...');

        if (window.google && window.google.maps) {
            console.log('ServiceLocationMap: Google Maps already loaded');
            setMapLoaded(true);
            return;
        }

        const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
        if (existingScript) {
            console.log('ServiceLocationMap: Script tag already exists, waiting for load...');
            if (existingScript.hasAttribute('data-loaded')) {
                setMapLoaded(true);
            } else {
                const handleScriptLoad = () => {
                    console.log('ServiceLocationMap: Google Maps script loaded (from existing tag)');
                    setMapLoaded(true);
                };
                existingScript.addEventListener('load', handleScriptLoad, { once: true });
                return () => {
                    existingScript.removeEventListener('load', handleScriptLoad);
                };
            }
            return;
        }

        console.log('ServiceLocationMap: Loading Google Maps script...');
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places`;
        script.async = true;
        script.defer = true;
        script.onload = () => {
            console.log('ServiceLocationMap: Google Maps script loaded successfully');
            script.setAttribute('data-loaded', 'true');
            setMapLoaded(true);
        };
        script.onerror = () => {
            console.error('ServiceLocationMap: Failed to load Google Maps script. Check your API key in .env: REACT_APP_GOOGLE_MAPS_API_KEY');
        };
        document.head.appendChild(script);
    }, [showMap]);

    // Initialize map when loaded
    useEffect(() => {
        if (showMap && mapLoaded && !mapInitialized) {
            console.log('ServiceLocationMap: Map loaded, scheduling initialization...');
            const timeoutId = setTimeout(() => {
                initializeMap();
            }, 300);

            return () => {
                clearTimeout(timeoutId);
            };
        }
    }, [showMap, mapLoaded, mapInitialized, initializeMap]);

    // Get current GPS location
    const getCurrentLocation = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const lat = position.coords.latitude;
                    const lng = position.coords.longitude;
                    setMapCenter({ lat, lng });

                    if (mapInstanceRef.current) {
                        mapInstanceRef.current.setCenter({ lat, lng });
                        mapInstanceRef.current.setZoom(15);
                        createMarker({ lat, lng }, mapInstanceRef.current);
                        if (circleRef.current) {
                            circleRef.current.setMap(null);
                        }
                    }

                    reverseGeocode(lat, lng);
                },
                () => {
                    alert('Unable to get your current location. Please ensure location access is enabled.');
                }
            );
        }
    };

    // Clear selected location
    const clearLocation = () => {
        if (markerRef.current) {
            markerRef.current.setMap(null);
            markerRef.current = null;
            if (onLocationChange) {
                onLocationChange(0, 0, '', '', '');
            }
        }
    };

    return (
        <div style={{ width: '100%' }}>
            <style>{`
                .sl-map-card {
                    background: #fff;
                    border-radius: 8px;
                    width: 100%;
                }
                .sl-map-wrapper {
                    padding: 16px 20px;
                }
                .sl-map-element {
                    width: 100%;
                    height: 400px;
                    border-radius: 8px;
                    border: 2px solid #3498db;
                    overflow: hidden;
                    box-shadow: 0 4px 8px rgba(52, 152, 219, 0.2);
                    background: #e5e7eb;
                }
                .sl-map-loading {
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 400px;
                    border-radius: 8px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    background-color: #f8f9fa;
                    color: #6c757d;
                    z-index: 10;
                }
                @media (max-width: 768px) {
                    .sl-map-card {
                        background: transparent;
                    }
                    .sl-map-wrapper {
                        padding: 0;
                    }
                    .sl-map-element {
                        height: 280px;
                    }
                    .sl-map-loading {
                        height: 280px;
                    }
                }
            `}</style>
            <div className="sl-map-card">
                <div className="sl-map-wrapper">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                        <div>
                            <h5 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: 600 }}>📍 Service Location Map</h5>
                            <p style={{ margin: 0, fontSize: '13px', color: '#64748b' }}>
                                {isEditing
                                    ? 'Click on the map to set your exact service location'
                                    : 'Service area is shown below'}
                            </p>
                        </div>
                    </div>

                    {showMap && (
                        <>
                            {isEditing && (
                                <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
                                    <button
                                        type="button"
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: '6px',
                                            padding: '7px 14px', borderRadius: '6px', fontSize: '13px',
                                            border: '1px solid #22c55e', color: '#fff', background: '#22c55e',
                                            cursor: 'pointer'
                                        }}
                                        onClick={getCurrentLocation}
                                    >
                                        <MapPin size={13} />
                                        Use GPS Location
                                    </button>

                                    {onConfirm && (
                                        <button
                                            type="button"
                                            style={{
                                                display: 'flex', alignItems: 'center', gap: '6px',
                                                padding: '7px 14px', borderRadius: '6px', fontSize: '13px',
                                                border: '1px solid #3498db', color: '#fff', background: '#3498db',
                                                cursor: 'pointer'
                                            }}
                                            onClick={handleConfirm}
                                        >
                                            Confirm Location
                                        </button>
                                    )}

                                    {markerRef.current && (
                                        <button
                                            type="button"
                                            style={{
                                                display: 'flex', alignItems: 'center', gap: '6px',
                                                padding: '7px 14px', borderRadius: '6px', fontSize: '13px',
                                                border: '1px solid #ef4444', color: '#ef4444', background: '#fff',
                                                cursor: 'pointer'
                                            }}
                                            onClick={clearLocation}
                                        >
                                            <X size={13} />
                                            Clear Pin
                                        </button>
                                    )}
                                </div>
                            )}

                            {/* Map Container */}
                            <div style={{ position: 'relative' }}>
                                <div
                                    ref={mapRef}
                                    className="sl-map-element"
                                />

                                {/* Open in Google Maps Button */}
                                {currentLocation.lat !== 0 && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const url = `https://www.google.com/maps/dir/?api=1&destination=${currentLocation.lat},${currentLocation.lng}`;
                                            window.open(url, '_blank');
                                        }}
                                        style={{
                                            position: 'absolute',
                                            bottom: '16px',
                                            right: '16px',
                                            backgroundColor: '#fff',
                                            color: '#1a73e8',
                                            border: '1px solid #dadce0',
                                            borderRadius: '8px',
                                            padding: '10px 16px',
                                            fontSize: '14px',
                                            fontWeight: 600,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            cursor: 'pointer',
                                            boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                                            zIndex: 5,
                                            transition: 'all 0.2s ease'
                                        }}
                                        onMouseOver={(e) => {
                                            e.currentTarget.style.backgroundColor = '#f8f9fa';
                                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.3)';
                                        }}
                                        onMouseOut={(e) => {
                                            e.currentTarget.style.backgroundColor = '#fff';
                                            e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.3)';
                                        }}
                                    >
                                        <Map size={16} />
                                        Get Directions
                                    </button>
                                )}

                                {/* Loading overlay */}
                                {(!mapLoaded || !mapInitialized) && (
                                    <div className="sl-map-loading">
                                        <div style={{
                                            width: '32px', height: '32px', border: '3px solid #e2e8f0',
                                            borderTopColor: '#3498db', borderRadius: '50%',
                                            animation: 'spin 1s linear infinite', marginBottom: '8px'
                                        }} />
                                        <div style={{ fontSize: '14px' }}>Loading map...</div>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ServiceLocationMap;
